import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import QRCode from "qrcode";
import { jsPDF } from "jspdf";
import api, { formatApiError } from "@/lib/api";
import { useAuth, usePerm } from "@/context/AuthContext";
import { StatusBadge, StatusDot } from "@/components/Status";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Trash2,
  RefreshCw,
  Eye,
  EyeOff,
  Copy,
  Download,
  Cable,
  Hash,
  Layers,
  UploadCloud,
  Users,
  PhoneCall,
  Activity,
  Siren,
  Server,
  Shield,
  Settings,
  Smartphone,
  AlertTriangle,
  Search,
  QrCode,
} from "lucide-react";
import NetworkTab from "@/components/hotel/NetworkTab";
import PhoneConfigDrawer from "@/components/hotel/PhoneConfigDrawer";
import GuestContactTab from "@/components/hotel/GuestContactTab";

// Fuseaux horaires proposés (IANA). Antilles/Martinique en tête (parc actuel).
const TIMEZONES = [
  "America/Martinique",
  "America/Guadeloupe",
  "America/Cayenne",
  "America/Port-au-Prince",
  "Europe/Paris",
  "Indian/Reunion",
  "UTC",
];

export default function HotelDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const canEditTrunks      = usePerm("can_edit_trunks");
  const canViewConfig      = usePerm("can_view_config");
  const canViewEmergency   = usePerm("can_view_emergency");
  const canEditNetwork     = usePerm("can_edit_network");
  const canViewScripts     = usePerm("can_view_scripts");
  const canEditVlan        = usePerm("can_edit_vlan");
  const canManageRooms     = usePerm("can_manage_rooms");
  const canProvision       = usePerm("can_provision");
  const canSyncPhones      = usePerm("can_sync_phones");
  const canManageSpeedDials= usePerm("can_manage_speed_dials");
  const canViewCdr         = usePerm("can_view_cdr");
  const [hotel, setHotel] = useState(null);
  const [vlan, setVlan] = useState("");
  const [sipPrefix, setSipPrefix] = useState("");
  const [tz, setTz] = useState("America/Martinique");
  const [logoUrl, setLogoUrl] = useState("");
  const [syncKey, setSyncKey] = useState(0);
  const [mt, setMt] = useState([]);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deletePassword, setDeletePassword] = useState("");
  const [deleting, setDeleting] = useState(false);

  const loadHotel = () =>
    api.get(`/hotels/${id}`).then((r) => {
      setHotel(r.data);
      setVlan(r.data.voice_vlan || "");
      setSipPrefix(r.data.sip_prefix || "");
      setTz(r.data.timezone || "America/Martinique");
      setLogoUrl(r.data.logo_url || "");
    });
  useEffect(() => {
    loadHotel();
    api.get("/mt-3cx").then(r => setMt(r.data)).catch(() => setMt([]));
  }, [id]);

  const pushConfig = async () => {
    const t = toast.loading("Push de la config vers le serveur Asterisk…");
    try {
      const { data } = await api.post(`/hotels/${id}/push`);
      toast.dismiss(t);
      data.ok ? toast.success(data.message) : toast.error(data.error);
      loadHotel();
    } catch (e) {
      toast.dismiss(t);
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const syncStatus = async () => {
    const t = toast.loading("Synchronisation du statut SIP…");
    try {
      const { data } = await api.post(`/hotels/${id}/sync`);
      toast.dismiss(t);
      data.ok
        ? toast.success(`${data.rooms_updated} chambre(s) mises à jour`)
        : toast.error(data.error);
      loadHotel();
      setSyncKey((k) => k + 1);
    } catch (e) {
      toast.dismiss(t);
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.post(`/hotels/${id}/delete`, {
        password: deletePassword,
        confirm_domain: deleteConfirm,
      });
      toast.success(`${hotel.name} supprimé`);
      navigate("/hotels");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setDeleting(false);
    }
  };

  const hotelBase = () => ({
    name: hotel.name,
    sip_domain: hotel.sip_domain,
    sip_server_id: hotel.sip_server_id,
    extension_prefix: hotel.extension_prefix || "1",
    sip_prefix: hotel.sip_prefix || "",
    voice_vlan: hotel.voice_vlan || "",
    timezone: hotel.timezone || "America/Martinique",
    contact_mode: hotel.contact_mode || "phones",
    logo_url: hotel.logo_url || "",
    trunk_mode: hotel.trunk_mode || "dedicated",
    shared_trunk_id: hotel.shared_trunk_id || "",
    ext_range: hotel.ext_range || "",
  });

  const saveTrunk = async (patch) => {
    try { await api.put(`/hotels/${id}`, { ...hotelBase(), ...patch }); toast.success("Trunk enregistré"); loadHotel(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveLogo = async () => {
    try {
      await api.put(`/hotels/${id}`, { ...hotelBase(), logo_url: logoUrl.trim() });
      toast.success("Logo enregistré");
      loadHotel();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const saveContactMode = async (mode) => {
    try {
      await api.put(`/hotels/${id}`, { ...hotelBase(), contact_mode: mode });
      toast.success("Mode de contact enregistré");
      loadHotel();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const saveVlan = async () => {
    try {
      await api.put(`/hotels/${id}`, { ...hotelBase(), voice_vlan: vlan });
      toast.success("VLAN voix enregistré");
      loadHotel();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const saveSipPrefix = async () => {
    try {
      await api.put(`/hotels/${id}`, { ...hotelBase(), sip_prefix: sipPrefix.trim() });
      toast.success("Préfixe SIP enregistré");
      loadHotel();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const saveTimezone = async () => {
    try {
      await api.put(`/hotels/${id}`, { ...hotelBase(), timezone: tz });
      toast.success("Fuseau horaire enregistré");
      loadHotel();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (!hotel)
    return <div className="font-mono text-sm text-zinc-400">Chargement…</div>;

  // Mode de contact de l'hôtel : phones (défaut) | qr | both.
  // Les chambres (numéros + QR) se gèrent dans TOUS les modes — l'onglet
  // Chambres reste donc visible partout (il s'adaptera au mode en Plan 3 :
  // SIP en phones/both, QR en qr/both). Les "Touches type 9" sont visibles
  // partout : en mode QR elles peuvent cibler les services Contact QR.
  const contactMode = hotel.contact_mode || "phones";
  const showPhoneKeys = true;                    // Touches type 9 (peuvent pointer vers un service QR)
  const showQr = contactMode !== "phones";       // onglet Contact QR
  const defaultTab = "rooms";

  return (
    <div data-testid="hotel-detail-page">
      <button
        onClick={() => navigate("/hotels")}
        className="mb-3 inline-flex items-center gap-1 text-[11px] text-zinc-400 hover:text-zinc-700 transition-colors"
      >
        <ArrowLeft className="h-3 w-3" /> Hôtels
      </button>

      {/* ── Hotel header card ─────────────────────────────────────── */}
      <div className="overflow-hidden rounded-sm border border-zinc-200 bg-white">

        {/* Row 1 : identity + actions */}
        <div className="flex items-start justify-between px-6 py-5">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">
              {hotel.server_name} · {hotel.city || "—"}
            </p>
            <h1 className="mt-1 font-head text-3xl font-black tracking-tighter text-zinc-900 leading-tight">
              {hotel.name}
            </h1>
            <div className="mt-1 font-mono text-sm text-zinc-400">{hotel.sip_domain}</div>
          </div>

          <div className="flex items-center gap-2 shrink-0 pl-4 pt-1">
            {canSyncPhones && (
              <button
                data-testid="sync-status-btn"
                onClick={syncStatus}
                className="inline-flex items-center gap-1.5 rounded-sm border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-600 hover:border-zinc-300 hover:text-zinc-900 transition-colors"
              >
                <RefreshCw className="h-3.5 w-3.5" /> Synchroniser
              </button>
            )}
            {canProvision && (
              <button
                data-testid="push-config-btn"
                onClick={pushConfig}
                className="inline-flex items-center gap-1.5 rounded-sm bg-zinc-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-zinc-700 transition-colors"
              >
                <UploadCloud className="h-3.5 w-3.5" /> Pousser vers serveur
              </button>
            )}
            {isSuper && (
              <button
                data-testid="delete-hotel-btn"
                onClick={() => { setDeleteConfirm(""); setDeletePassword(""); setDeleteOpen(true); }}
                className="inline-flex items-center gap-1.5 rounded-sm border border-zinc-200 px-2.5 py-1.5 text-xs text-zinc-400 hover:border-red-200 hover:text-red-600 hover:bg-red-50 transition-colors"
                title="Supprimer l'hôtel"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Row 2 : stats */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 border-t border-zinc-100 bg-zinc-50/50 px-6 py-2.5">
          <StatusBadge status={hotel.trunk_status} />
          {contactMode !== "qr" ? (
            <span className="flex items-center gap-1.5 font-mono text-xs text-zinc-600">
              <StatusDot online={hotel.rooms_online > 0} />
              {hotel.rooms_online}/{hotel.rooms_count} chambres en ligne
            </span>
          ) : (
            <span className="font-mono text-xs text-zinc-600">{hotel.rooms_count} chambres</span>
          )}
          {hotel.phones_bound > 0 && (
            <span className="flex items-center gap-1 font-mono text-xs text-zinc-500">
              <span className="text-emerald-500">✓</span>
              {hotel.phones_bound}/{hotel.rooms_count} postes provisionnés
            </span>
          )}
        </div>

        {/* Row 3 : config fields (VLAN, prefix) */}
        {(isSuper || canEditVlan) && (
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-zinc-100 px-6 py-3">
            {contactMode !== "qr" && (
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">VLAN voix</span>
                <input
                  data-testid="hotel-vlan-edit"
                  value={vlan}
                  onChange={(e) => setVlan(e.target.value)}
                  placeholder="—"
                  className="w-20 rounded-sm border border-zinc-200 px-2 py-1 font-mono text-xs focus:border-blue-400 focus:outline-none"
                />
                <button
                  data-testid="save-vlan-btn"
                  onClick={saveVlan}
                  disabled={vlan === (hotel.voice_vlan || "")}
                  className="rounded-sm border border-zinc-200 bg-white px-2 py-1 text-xs text-zinc-600 hover:bg-zinc-50 disabled:opacity-30 transition-colors"
                >
                  Enregistrer
                </button>
              </div>
            )}
            {isSuper && contactMode !== "qr" && (
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Préfixe SIP</span>
                <input
                  value={sipPrefix}
                  onChange={(e) => setSipPrefix(e.target.value)}
                  placeholder="—"
                  className="w-20 rounded-sm border border-zinc-200 px-2 py-1 font-mono text-xs focus:border-blue-400 focus:outline-none"
                />
                <button
                  onClick={saveSipPrefix}
                  disabled={sipPrefix.trim() === (hotel.sip_prefix || "")}
                  className="rounded-sm border border-zinc-200 bg-white px-2 py-1 text-xs text-zinc-600 hover:bg-zinc-50 disabled:opacity-30 transition-colors"
                >
                  Enregistrer
                </button>
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Fuseau horaire</span>
              <select
                data-testid="hotel-tz-edit"
                value={tz}
                onChange={(e) => setTz(e.target.value)}
                className="rounded-sm border border-zinc-200 px-2 py-1 font-mono text-xs focus:border-blue-400 focus:outline-none"
              >
                {TIMEZONES.map((z) => (
                  <option key={z} value={z}>{z}</option>
                ))}
              </select>
              <button
                data-testid="save-tz-btn"
                onClick={saveTimezone}
                disabled={tz === (hotel.timezone || "America/Martinique")}
                className="rounded-sm border border-zinc-200 bg-white px-2 py-1 text-xs text-zinc-600 hover:bg-zinc-50 disabled:opacity-30 transition-colors"
              >
                Enregistrer
              </button>
            </div>
            {isSuper && (
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Mode contact</span>
                <select
                  data-testid="hotel-contact-mode"
                  value={hotel.contact_mode || "phones"}
                  onChange={(e) => saveContactMode(e.target.value)}
                  className="rounded-sm border border-zinc-200 px-2 py-1 text-xs focus:border-blue-400 focus:outline-none"
                >
                  <option value="phones">Téléphones</option>
                  <option value="qr">100% QR</option>
                  <option value="both">Téléphones + QR</option>
                </select>
              </div>
            )}
            {isSuper && (
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Trunk</span>
                <select data-testid="hotel-trunk-mode" value={hotel.trunk_mode || "dedicated"}
                  onChange={(e) => saveTrunk({ trunk_mode: e.target.value })}
                  className="rounded-sm border border-zinc-200 px-2 py-1 text-xs focus:border-blue-400 focus:outline-none">
                  <option value="dedicated">Dédié</option>
                  <option value="shared">Partagé (3CX MT)</option>
                </select>
                {hotel.trunk_mode === "shared" && (
                  <select data-testid="hotel-shared-trunk" value={hotel.shared_trunk_id || ""}
                    onChange={(e) => saveTrunk({ shared_trunk_id: e.target.value })}
                    className="rounded-sm border border-zinc-200 px-2 py-1 text-xs focus:border-blue-400 focus:outline-none">
                    <option value="">— choisir —</option>
                    {mt.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.host})</option>)}
                  </select>
                )}
              </div>
            )}
          </div>
        )}
        {isSuper && showQr && (
          <div className="flex flex-wrap items-center gap-2 border-t border-zinc-100 px-6 py-2.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Logo QR</span>
            {logoUrl
              ? <img src={logoUrl} alt="" className="h-7 w-7 rounded-sm border border-zinc-200 object-cover" />
              : <span className="grid h-7 w-7 place-items-center rounded-sm bg-zinc-900 text-xs font-bold text-amber-300">{(hotel.name || "H").charAt(0).toUpperCase()}</span>}
            <input
              data-testid="hotel-logo-url"
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
              placeholder="https://…/logo.png (vide = initiale de l'hôtel)"
              className="min-w-[240px] flex-1 rounded-sm border border-zinc-200 px-2 py-1 font-mono text-xs focus:border-blue-400 focus:outline-none"
            />
            <button data-testid="hotel-logo-save" onClick={saveLogo} className="rounded-sm border border-zinc-200 bg-white px-2 py-1 text-xs text-zinc-600 hover:bg-zinc-50 transition-colors">Enregistrer</button>
          </div>
        )}
        {!(isSuper || canEditVlan) && (hotel.voice_vlan) && (
          <div className="border-t border-zinc-100 px-6 py-2.5 font-mono text-xs text-zinc-400">
            VLAN voix : {hotel.voice_vlan}
          </div>
        )}
      </div>

      <Tabs key={contactMode} defaultValue={defaultTab} className="mt-6">
        <TabsList className="rounded-sm bg-zinc-100">
          <TabsTrigger value="rooms" data-testid="tab-rooms" className="rounded-sm">
            <Hash className="mr-1.5 h-3.5 w-3.5" /> Chambres
          </TabsTrigger>
          {canEditTrunks && (
            <TabsTrigger value="trunk" data-testid="tab-trunk" className="rounded-sm">
              <Cable className="mr-1.5 h-3.5 w-3.5" /> Trunk 3CX
            </TabsTrigger>
          )}
          {canManageSpeedDials && showPhoneKeys && (
            <TabsTrigger value="keys" data-testid="tab-keys" className="rounded-sm">
              <Layers className="mr-1.5 h-3.5 w-3.5" /> Touches type 9
            </TabsTrigger>
          )}
          {canManageSpeedDials && showQr && (
            <TabsTrigger value="guest-contact" data-testid="tab-guest-contact" className="rounded-sm">
              <QrCode className="mr-1.5 h-3.5 w-3.5" /> Contact QR
            </TabsTrigger>
          )}
          {canViewConfig && (
            <TabsTrigger value="config" data-testid="tab-config" className="rounded-sm">
              <Download className="mr-1.5 h-3.5 w-3.5" /> Configuration
            </TabsTrigger>
          )}
          {canViewCdr && (
            <TabsTrigger value="cdr" data-testid="tab-cdr" className="rounded-sm">
              <PhoneCall className="mr-1.5 h-3.5 w-3.5" /> Appels (CDR)
            </TabsTrigger>
          )}
          {canViewEmergency && (
            <TabsTrigger value="emergency" data-testid="tab-emergency" className="rounded-sm">
              <Siren className="mr-1.5 h-3.5 w-3.5" /> Urgences
            </TabsTrigger>
          )}
          {canEditNetwork && (
            <TabsTrigger value="network" data-testid="tab-network" className="rounded-sm">
              <Shield className="mr-1.5 h-3.5 w-3.5" /> Réseau
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="rooms">
          <RoomsTab hotelId={id} hotel={hotel} onChange={loadHotel} syncKey={syncKey} />
        </TabsContent>
        {canEditTrunks && (
          <TabsContent value="trunk">
            <TrunkTab hotelId={id} onChange={loadHotel} />
          </TabsContent>
        )}
        {canManageSpeedDials && showPhoneKeys && (
          <TabsContent value="keys">
            <KeysTab hotelId={id} />
          </TabsContent>
        )}
        {canManageSpeedDials && showQr && (
          <TabsContent value="guest-contact">
            <GuestContactTab hotelId={id} />
          </TabsContent>
        )}
        {canViewConfig && (
          <TabsContent value="config">
            <ConfigTab hotelId={id} hotelName={hotel.name} />
          </TabsContent>
        )}
        {canViewCdr && (
          <TabsContent value="cdr">
            <CdrTab hotelId={id} />
          </TabsContent>
        )}
        {canViewEmergency && (
          <TabsContent value="emergency">
            <EmergencyTab hotelId={id} />
          </TabsContent>
        )}
        {canEditNetwork && (
          <TabsContent value="network">
            <NetworkTab hotelId={id} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function Secret({ value }) {
  const [show, setShow] = useState(false);
  return (
    <span className="inline-flex items-center gap-1.5 font-mono text-xs">
      <span className={show ? "text-zinc-800" : "tracking-widest text-zinc-400"}>
        {show ? value : "••••••••"}
      </span>
      <button onClick={() => setShow(!show)} className="text-zinc-400 hover:text-zinc-700">
        {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
      </button>
      <button
        onClick={() => {
          navigator.clipboard.writeText(value);
          toast.success("Copié");
        }}
        className="text-zinc-400 hover:text-zinc-700"
      >
        <Copy className="h-3.5 w-3.5" />
      </button>
    </span>
  );
}

const PHONE_MODELS_MAP = {
  yealink: ["T33G","T42S","T42U","T43U","T46S","T46U","T48S","T48U","T54W","T57W","T58A","W52P","W56P","W73P"],
  fanvil:  ["X4","X4U","X4G","X5U","X6","X6U","X7","X7C","V62","V64","V65"],
  cisco:   ["CP-3905"],
};

const OUI_VENDOR_MAP = {
  "00:15:65":"yealink","80:5e:c0":"yealink","00:1d:e9":"yealink",
  "00:a8:59":"fanvil","08:a8:59":"fanvil","38:8b:59":"fanvil","48:7b:55":"fanvil",
  "00:1f:c1":"cisco","00:17:e0":"cisco","7c:95:f3":"cisco","88:43:e1":"cisco",
};

function detectVendor(mac) {
  if (!mac) return null;
  const oui = mac.trim().replace(/-/g, ":").toLowerCase().slice(0, 8);
  return OUI_VENDOR_MAP[oui] || null;
}

function PhoneFields({ vendor, model, mac, onChange, showMac = true, existingModels = [] }) {
  return (
    <div className="mt-3 border-t border-zinc-100 pt-3">
      <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-zinc-400">Poste téléphonique (optionnel)</p>
      {existingModels.length > 0 && (
        <div className="mb-3">
          <Lbl>Copier depuis un modèle existant</Lbl>
          <select
            defaultValue=""
            onChange={(e) => {
              if (!e.target.value) return;
              const [v, m] = e.target.value.split("|");
              onChange({ phone_vendor: v, phone_model: m || "" });
            }}
            className="mt-1 w-full rounded-sm border border-zinc-200 bg-white px-2 py-1.5 text-xs text-zinc-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
          >
            <option value="">— Choisir un modèle en place —</option>
            {existingModels.map(({ vendor: v, model: m, room_number }) => (
              <option key={room_number} value={`${v}|${m}`}>
                Ch. {room_number} — {v.charAt(0).toUpperCase() + v.slice(1)}{m ? ` ${m}` : ""}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className={`grid gap-3 ${showMac ? "grid-cols-1" : "grid-cols-2"}`}>
        {showMac && (
          <div>
            <Lbl>Adresse MAC</Lbl>
            <Input
              value={mac}
              onChange={(e) => {
                const newMac = e.target.value;
                const detected = !vendor ? detectVendor(newMac) : null;
                onChange({ mac: newMac, ...(detected ? { phone_vendor: detected, phone_model: "" } : {}) });
              }}
              placeholder="AA:BB:CC:DD:EE:FF"
              className="mt-1 rounded-sm font-mono text-xs"
            />
          </div>
        )}
        <div className={showMac ? "grid grid-cols-2 gap-3" : "contents"}>
          <div>
            <Lbl>Fabricant</Lbl>
            <select
              value={vendor}
              onChange={(e) => onChange({ phone_vendor: e.target.value, phone_model: "" })}
              className="mt-1 w-full rounded-sm border border-zinc-200 bg-white px-2 py-1.5 text-xs text-zinc-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
            >
              <option value="">— Aucun —</option>
              <option value="yealink">Yealink</option>
              <option value="fanvil">Fanvil</option>
              <option value="cisco">Cisco</option>
            </select>
          </div>
          {vendor && PHONE_MODELS_MAP[vendor] && (
            <div>
              <Lbl>Modèle</Lbl>
              <select
                value={model}
                onChange={(e) => onChange({ phone_model: e.target.value })}
                className="mt-1 w-full rounded-sm border border-zinc-200 bg-white px-2 py-1.5 text-xs text-zinc-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
              >
                <option value="">— Choisir —</option>
                {PHONE_MODELS_MAP[vendor].map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------------------- Rooms ---------------------------- */
function RoomsTab({ hotelId, hotel, onChange, syncKey }) {
  const canManageRooms  = usePerm("can_manage_rooms");
  const canProvision    = usePerm("can_provision");
  const canViewScripts  = usePerm("can_view_scripts");

  const mode = hotel?.contact_mode || "phones";
  const showSip = mode !== "qr";    // colonnes SIP (User SIP, Secret, Poste MAC, IP)
  const showQr = mode !== "phones"; // colonne QR + planche PDF

  const [rooms, setRooms] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [qrRoom, setQrRoom] = useState(null);   // chambre dont on affiche le QR
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [drawerRoomId, setDrawerRoomId] = useState(null);
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [form, setForm] = useState({ room_number: "", mac: "", phone_vendor: "", phone_model: "" });
  const [bulk, setBulk] = useState({ start: 101, count: 10, phone_vendor: "", phone_model: "" });
  const [filterVendor, setFilterVendor] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [search, setSearch] = useState("");

  // Filtre partagé (tableau + pagination) : poste/vendor, statut, recherche texte.
  const roomMatches = (r) => {
    if (filterVendor === "__none__") { if (r.phone_vendor) return false; }
    else if (filterVendor === "__unregistered__") { if (r.status === "registered") return false; }
    else if (filterVendor && r.phone_vendor !== filterVendor) return false;
    if (filterStatus && r.status !== filterStatus) return false;
    const q = search.trim().toLowerCase();
    if (q) {
      const hay = [r.room_number, r.extension, r.sip_username, r.mac, r.last_ip]
        .filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  };
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 30;
  const [selected, setSelected] = useState(new Set());
  const [bulkPhone, setBulkPhone] = useState({ vendor: "", model: "" });
  const [applyingBulk, setApplyingBulk] = useState(false);
  const [provOpen, setProvOpen] = useState(false);
  const [macCsv, setMacCsv] = useState("");
  const [rebootPhones, setRebootPhones] = useState([]);

  const load = async (silent = false) => {
    if (!silent) setSyncing(true);
    try {
      const r = await api.get(`/hotels/${hotelId}/rooms`);
      setRooms(r.data);
    } finally {
      if (!silent) setSyncing(false);
    }
  };

  const loadRebootPhones = () =>
    api.get(`/hotels/${hotelId}/phones-to-reboot`)
      .then((r) => setRebootPhones(r.data?.phones || []))
      .catch(() => setRebootPhones([]));

  // Initial load + reload when parent triggers sync
  useEffect(() => { load(); loadRebootPhones(); }, [hotelId, syncKey]);

  // Auto-poll every 30s for real-time status
  useEffect(() => {
    const id = setInterval(() => load(true), 30000);
    return () => clearInterval(id);
  }, [hotelId]);

  // Génère le data URL du QR quand une chambre est sélectionnée
  useEffect(() => {
    if (!qrRoom?.qr_url) { setQrDataUrl(""); return; }
    QRCode.toDataURL(qrRoom.qr_url, { width: 320, margin: 1 }).then(setQrDataUrl).catch(() => setQrDataUrl(""));
  }, [qrRoom]);

  const downloadPng = () => {
    if (!qrDataUrl) return;
    const a = document.createElement("a");
    a.href = qrDataUrl; a.download = `qr-chambre-${qrRoom.room_number}.png`; a.click();
  };

  const generatePdf = async () => {
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const withQr = rooms.filter((r) => r.qr_url);
    const cols = 3, cellW = 65, cellH = 80, mX = 8, mY = 12;
    for (let i = 0; i < withQr.length; i++) {
      const r = withQr[i];
      const col = i % cols, row = Math.floor(i / cols) % 3;
      if (i > 0 && col === 0 && row === 0) doc.addPage();
      const x = mX + col * cellW, y = mY + row * cellH;
      const dataUrl = await QRCode.toDataURL(r.qr_url, { width: 256, margin: 1 });
      doc.addImage(dataUrl, "PNG", x, y, 55, 55);
      doc.setFontSize(12); doc.text(`Chambre ${r.room_number}`, x + 27, y + 62, { align: "center" });
    }
    doc.save(`qr-${hotelId}.pdf`);
  };

  const applyBulkPhone = async () => {
    if (!bulkPhone.vendor) return;
    setApplyingBulk(true);
    try {
      await Promise.all([...selected].map((id) =>
        api.put(`/rooms/${id}`, { phone_vendor: bulkPhone.vendor, phone_model: bulkPhone.model })
      ));
      toast.success(`Poste appliqué à ${selected.size} chambre(s)`);
      setSelected(new Set());
      setBulkPhone({ vendor: "", model: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setApplyingBulk(false);
    }
  };

  const existingModels = rooms
    .filter((r) => r.phone_vendor)
    .map((r) => ({ vendor: r.phone_vendor, model: r.phone_model || "", room_number: r.room_number }));

  const add = async () => {
    try {
      await api.post(`/hotels/${hotelId}/rooms`, {
        room_number: form.room_number,
        extension: form.room_number,
        mac: form.mac || "",
        phone_vendor: form.phone_vendor || "",
        phone_model: form.phone_model || "",
      });
      toast.success("Chambre provisionnée");
      setOpen(false);
      setForm({ room_number: "", mac: "", phone_vendor: "", phone_model: "" });
      load();
      onChange();
      api.post(`/hotels/${hotelId}/push`).catch(() => {});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const addBulk = async () => {
    try {
      const { data } = await api.post(`/hotels/${hotelId}/rooms/bulk`, {
        start: Number(bulk.start),
        count: Number(bulk.count),
        phone_vendor: bulk.phone_vendor || "",
        phone_model: bulk.phone_model || "",
      });
      toast.success(`${data.created} chambres créées`);
      setBulkOpen(false);
      load();
      onChange();
      api.post(`/hotels/${hotelId}/push`).catch(() => {});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const del = async (rid) => {
    try {
      await api.delete(`/rooms/${rid}`);
      toast.success("Chambre supprimée");
      load();
      onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const exportCsv = async () => {
    try {
      const { data } = await api.get(`/hotels/${hotelId}/rooms/export`);
      const blob = new Blob([data.content], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename || "comptes-sip.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${data.count} comptes SIP exportés`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const provisionPhones = async () => {
    const t = toast.loading("Génération et push des configs téléphone…");
    try {
      const { data } = await api.post(`/hotels/${hotelId}/provision-phones`);
      toast.dismiss(t);
      const failed = (data.files || []).filter((f) => !f.ok);
      if (data.ok) {
        toast.success(`${data.pushed}/${data.total} fichier(s) poussé(s) vers l'agent`);
      } else {
        toast.warning(`${data.pushed}/${data.total} poussé(s) — ${failed.length} erreur(s)`);
      }
      load(true);
    } catch (e) {
      toast.dismiss(t);
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const provisionRoom = async (room) => {
    const t = toast.loading(`Provisioning chambre ${room.room_number}…`);
    try {
      const { data } = await api.post(`/rooms/${room.id}/provision-phone`);
      toast.dismiss(t);
      data.ok
        ? toast.success(`Config poussée (${data.filename})`)
        : toast.error(data.error);
    } catch (e) {
      toast.dismiss(t);
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const importMacs = async () => {
    try {
      const { data } = await api.post(`/hotels/${hotelId}/rooms/import-macs`, { csv: macCsv });
      toast.success(`${data.updated} MAC importées${data.errors?.length ? `, ${data.errors.length} ignorées` : ""}`);
      setMacCsv("");
      setProvOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const downloadRelay = async () => {
    try {
      const { data } = await api.get(`/hotels/${hotelId}/provision-relay-script`);
      const blob = new Blob([data.script], { type: "text/x-shellscript" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const downloadMikrotikScript = async () => {
    try {
      const { data } = await api.get(`/hotels/${hotelId}/mikrotik-tftp-script`);
      const blob = new Blob([data.script], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="mt-4">
      {rebootPhones.length > 0 && (
        <div className="mb-4 overflow-hidden rounded-sm border border-amber-300 bg-amber-50">
          <div className="flex items-start gap-3 px-4 py-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-amber-900">
                {rebootPhones.length} poste{rebootPhones.length > 1 ? "s" : ""} à redémarrer
              </p>
              <p className="mt-0.5 text-xs leading-relaxed text-amber-800">
                Ces postes tentent de se connecter mais échouent à l'authentification — ils ont une
                configuration obsolète. <strong>Redémarrez-les</strong> (débrancher/rebrancher) ou
                faites un <strong>reset usine</strong> : ils récupéreront automatiquement leur
                nouvelle configuration via TFTP et se reconnecteront.
              </p>
              <div className="mt-2.5 overflow-hidden rounded-sm border border-amber-200 bg-white/70">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-amber-100 text-left text-[9px] uppercase tracking-[0.15em] text-amber-700/70">
                      <th className="px-3 py-1.5 font-bold">Chambre</th>
                      <th className="px-3 py-1.5 font-bold">Extension</th>
                      <th className="px-3 py-1.5 font-bold">IP du poste</th>
                      <th className="px-3 py-1.5 font-bold">Tentatives</th>
                      <th className="px-3 py-1.5 font-bold">Dernier essai</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rebootPhones.map((p) => (
                      <tr key={p.extension} className="border-b border-amber-50 last:border-0">
                        <td className="px-3 py-1.5 font-medium text-zinc-800 tabular-nums">{p.room_number || "—"}</td>
                        <td className="px-3 py-1.5 font-mono text-zinc-600 tabular-nums">{p.extension}</td>
                        <td className="px-3 py-1.5 font-mono text-[11px] text-zinc-500">{p.ip}</td>
                        <td className="px-3 py-1.5 font-mono text-zinc-500 tabular-nums">{p.attempts}</td>
                        <td className="px-3 py-1.5 font-mono text-[11px] text-zinc-400">{p.last_attempt}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <button
              onClick={loadRebootPhones}
              title="Rafraîchir"
              className="shrink-0 rounded-sm border border-amber-200 bg-white/60 p-1.5 text-amber-700 hover:bg-white transition-colors"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
      <div className="mb-3 flex justify-end gap-2">
        {syncing && (
          <span className="flex items-center gap-1 text-[11px] text-zinc-400">
            <RefreshCw className="h-3 w-3 animate-spin" /> Mise à jour…
          </span>
        )}
        {showSip && canProvision && rooms.some((r) => r.mac && r.phone_vendor) && (
          <Button
            data-testid="provision-phones-btn"
            onClick={provisionPhones}
            className="rounded-sm bg-zinc-900 hover:bg-zinc-800"
          >
            <UploadCloud className="mr-1.5 h-3.5 w-3.5" /> Provisionner les postes
          </Button>
        )}
        {showSip && canViewScripts && (
          <Dialog open={provOpen} onOpenChange={setProvOpen}>
            <DialogTrigger asChild>
              <Button data-testid="provisioning-btn" variant="outline" className="rounded-sm">
                <Server className="mr-1 h-4 w-4" /> Provisioning postes
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader>
                <DialogTitle>Provisioning des postes (Cisco 3905)</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Lbl>Import MAC en lot (CSV : extension,MAC)</Lbl>
                  <textarea
                    data-testid="mac-csv-input"
                    value={macCsv}
                    onChange={(e) => setMacCsv(e.target.value)}
                    rows={5}
                    placeholder={"101,E069BAE82458\n102,001122334455"}
                    className="mt-1 w-full rounded-sm border border-zinc-200 p-2 font-mono text-xs"
                  />
                  <Button data-testid="import-macs-btn" onClick={importMacs} disabled={!macCsv.trim()} className="mt-2 rounded-sm bg-zinc-900 hover:bg-zinc-800">
                    Importer les MAC
                  </Button>
                </div>
                <div className="border-t border-zinc-100 pt-3 space-y-3">
                  <div>
                    <p className="text-xs font-semibold text-zinc-700">Option A — MikroTik TFTP (recommandé)</p>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      Script RouterOS : le MikroTik télécharge les SEP&lt;MAC&gt;.cnf.xml depuis HotelSIP
                      et les sert localement aux postes via TFTP (LAN direct, pas de NAT).
                      Configurer DHCP option 150 → IP du MikroTik.
                    </p>
                    <Button data-testid="mikrotik-script-btn" variant="outline" onClick={downloadMikrotikScript} className="mt-2 rounded-sm">
                      <Download className="mr-1 h-4 w-4" /> Script MikroTik (.rsc)
                    </Button>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-zinc-700">Option B — Relai LXC Debian</p>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      Alternative : relai TFTP sur un LXC Debian 12 du LAN (DHCP option 150 → ce LXC).
                    </p>
                    <Button data-testid="relay-script-btn" variant="outline" onClick={downloadRelay} className="mt-2 rounded-sm">
                      <Download className="mr-1 h-4 w-4" /> Script relai Debian (.sh)
                    </Button>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
        <Button
          data-testid="export-rooms-btn"
          variant="outline"
          className="rounded-sm"
          onClick={exportCsv}
          disabled={rooms.length === 0}
        >
          <Download className="mr-1 h-4 w-4" /> Exporter comptes SIP (CSV)
        </Button>
        {showQr && (
          <Button data-testid="gen-qr-pdf" onClick={generatePdf} variant="outline" className="rounded-sm">
            <QrCode className="mr-1.5 h-3.5 w-3.5" /> Générer les QR (PDF)
          </Button>
        )}
        {canManageRooms && (
          <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
            <DialogTrigger asChild>
              <Button data-testid="bulk-rooms-btn" variant="outline" className="rounded-sm">
                <Layers className="mr-1 h-4 w-4" /> Provisionner en lot
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader>
                <DialogTitle className="font-head tracking-tight">Provisioning en lot</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 py-2">
                <div>
                  <Lbl>N° première chambre</Lbl>
                  <Input data-testid="bulk-start-input" value={bulk.start} onChange={(e) => setBulk({ ...bulk, start: e.target.value })} className="mt-1 rounded-sm font-mono text-sm" />
                </div>
                <div>
                  <Lbl>Nombre de chambres</Lbl>
                  <Input data-testid="bulk-count-input" value={bulk.count} onChange={(e) => setBulk({ ...bulk, count: e.target.value })} className="mt-1 rounded-sm font-mono text-sm" />
                </div>
              </div>
              {showSip && (
                <PhoneFields
                  vendor={bulk.phone_vendor}
                  model={bulk.phone_model}
                  mac=""
                  showMac={false}
                  existingModels={existingModels}
                  onChange={(patch) => setBulk((b) => ({ ...b, ...patch }))}
                />
              )}
              <DialogFooter>
                <Button data-testid="save-bulk-btn" onClick={addBulk} className="rounded-sm bg-blue-600 hover:bg-blue-700">
                  Générer
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {canManageRooms && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-room-btn" className="rounded-sm bg-blue-600 hover:bg-blue-700">
                <Plus className="mr-1 h-4 w-4" /> Ajouter une chambre
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader>
                <DialogTitle className="font-head tracking-tight">Nouvelle chambre SIP</DialogTitle>
              </DialogHeader>
              <div className="py-2">
                <Lbl>N° de chambre = extension SIP</Lbl>
                <Input data-testid="room-number-input" value={form.room_number} onChange={(e) => setForm({ ...form, room_number: e.target.value })} className="mt-1 rounded-sm font-mono text-sm" placeholder="101" />
                <p className="mt-1 text-xs text-zinc-500">L'extension SIP sera identique au numéro de chambre.</p>
              </div>
              {showSip && (
                <PhoneFields
                  vendor={form.phone_vendor}
                  model={form.phone_model}
                  mac={form.mac}
                  showMac={true}
                  existingModels={existingModels}
                  onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
                />
              )}
              <DialogFooter>
                <Button data-testid="save-room-btn" onClick={add} disabled={!form.room_number} className="rounded-sm bg-blue-600 hover:bg-blue-700">
                  {showSip ? "Provisionner" : "Ajouter la chambre"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {rooms.length > 0 && (
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <select
            value={filterVendor}
            onChange={(e) => { setFilterVendor(e.target.value); setPage(1); }}
            className="rounded-sm border border-zinc-200 bg-white px-2 py-1.5 text-xs text-zinc-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
          >
            <option value="">Tous les postes</option>
            <option value="__none__">Sans poste configuré</option>
            {showSip && <option value="__unregistered__">Non enregistrés</option>}
            {[...new Set(rooms.map((r) => r.phone_vendor).filter(Boolean))].sort().map((v) => (
              <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>
            ))}
          </select>
          {showSip && (
            <select
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
              className="rounded-sm border border-zinc-200 bg-white px-2 py-1.5 text-xs text-zinc-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
            >
              <option value="">Tous les statuts</option>
              <option value="registered">En ligne</option>
              <option value="offline">Hors ligne</option>
            </select>
          )}
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
            <input
              data-testid="room-search"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Rechercher (chambre, ext, MAC, IP…)"
              className="w-64 rounded-sm border border-zinc-200 bg-white py-1.5 pl-7 pr-2 text-xs text-zinc-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-sm border border-zinc-200 bg-white">
        <table className="w-full text-sm">
          {(() => {
            const filteredRooms = rooms.filter(roomMatches);
            const totalPages = Math.max(1, Math.ceil(filteredRooms.length / PAGE_SIZE));
            const safePage = Math.min(page, totalPages);
            const pagedRooms = filteredRooms.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
            const allSelected = filteredRooms.length > 0 && filteredRooms.every((r) => selected.has(r.id));
            const someSelected = filteredRooms.some((r) => selected.has(r.id));
            const toggleAll = () => {
              if (allSelected) {
                setSelected((s) => { const n = new Set(s); filteredRooms.forEach((r) => n.delete(r.id)); return n; });
              } else {
                setSelected((s) => { const n = new Set(s); filteredRooms.forEach((r) => n.add(r.id)); return n; });
              }
            };
            return (<>
              <thead>
                <tr className="border-b border-zinc-200 text-left text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                  <th className="w-8 px-3 py-2">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                      onChange={toggleAll}
                      className="cursor-pointer rounded-sm"
                    />
                  </th>
                  <th className="px-4 py-2 font-bold">Chambre</th>
                  <th className="px-4 py-2 font-bold">Extension</th>
                  {showSip && (<>
                    <th className="px-4 py-2 font-bold">User SIP</th>
                    <th className="px-4 py-2 font-bold">Secret</th>
                    <th className="px-4 py-2 font-bold">Poste (MAC · vendor)</th>
                    <th className="px-4 py-2 font-bold">IP</th>
                  </>)}
                  {showQr && <th className="px-4 py-2 font-bold">QR</th>}
                  {showSip && <th className="px-4 py-2 font-bold">Statut</th>}
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {pagedRooms.map((r) => (
              <tr key={r.id} data-testid={`room-row-${r.room_number}`} className={`border-b border-zinc-100 last:border-0 hover:bg-zinc-50/70 ${selected.has(r.id) ? "bg-blue-50" : ""}`}>
                <td className="w-8 px-3 py-1.5">
                  <input
                    type="checkbox"
                    checked={selected.has(r.id)}
                    onChange={(e) => setSelected((s) => { const n = new Set(s); e.target.checked ? n.add(r.id) : n.delete(r.id); return n; })}
                    className="cursor-pointer rounded-sm"
                  />
                </td>
                <td className="px-4 py-1.5 font-medium text-zinc-900 tabular-nums">{r.room_number}</td>
                <td className="px-4 py-1.5 font-mono text-xs text-zinc-600 tabular-nums">{r.extension}</td>
                {showSip && (<>
                  <td className="px-4 py-1.5 font-mono text-xs text-zinc-600">{r.sip_username}</td>
                  <td className="px-4 py-1.5"><Secret value={r.sip_password} /></td>
                  <td className="px-4 py-1.5">
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono text-[11px] text-zinc-500">{r.mac || <span className="text-zinc-300">—</span>}</span>
                      {(r.phone_vendor || r.phone_model) && (
                        <span className="text-[10px] text-zinc-400">{r.phone_vendor}{r.phone_model ? ` ${r.phone_model}` : ""}</span>
                      )}
                      {r.provision_status === "bound" && (
                        <span className="rounded-sm bg-emerald-50 px-1 py-0.5 text-[9px] font-bold uppercase tracking-wide text-emerald-700">✓</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-1.5 font-mono text-[11px] text-zinc-500 tabular-nums">
                    {r.status === "registered" && r.last_ip ? r.last_ip : <span className="text-zinc-300">—</span>}
                  </td>
                </>)}
                {showQr && (
                  <td className="px-4 py-1.5">
                    {r.qr_url ? (
                      <button data-testid={`qr-btn-${r.room_number}`} onClick={() => setQrRoom(r)}
                        className="rounded-sm border border-zinc-200 px-2 py-1 text-[11px] text-zinc-600 hover:border-blue-300 hover:text-blue-600">
                        Voir QR
                      </button>
                    ) : <span className="text-zinc-300">—</span>}
                  </td>
                )}
                {showSip && (
                  <td className="px-4 py-1.5">
                    <div className="flex items-center gap-1.5">
                      <StatusBadge status={r.status} />
                      {r.status !== "registered" && r.last_seen_at && (
                        <span className="font-mono text-[10px] text-zinc-400">
                          {new Date(r.last_seen_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: hotel?.timezone || "America/Martinique" })}
                        </span>
                      )}
                    </div>
                  </td>
                )}
                <td className="px-4 py-1.5">
                  <div className="flex items-center justify-end gap-1.5">
                    {canProvision && r.mac && r.phone_vendor && (
                      <button type="button" onClick={() => provisionRoom(r)} title="Pousser la config téléphone" className="text-zinc-300 hover:text-emerald-600 transition-colors">
                        <Smartphone className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {showSip && (
                      <button type="button" onClick={() => setDrawerRoomId(r.id)} title="Configurer le téléphone" className="text-zinc-300 hover:text-blue-600 transition-colors">
                        <Settings className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {canManageRooms && (
                      <button data-testid={`delete-room-${r.room_number}`} onClick={() => del(r.id)} title="Supprimer" className="text-zinc-300 hover:text-red-600 transition-colors">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
                {rooms.length === 0 && (
                  <tr>
                    <td colSpan={4 + (showSip ? 5 : 0) + (showQr ? 1 : 0)} className="px-4 py-8 text-center text-sm text-zinc-400">
                      Aucune chambre. Provisionnez vos comptes SIP.
                    </td>
                  </tr>
                )}
                {filteredRooms.length === 0 && rooms.length > 0 && (
                  <tr>
                    <td colSpan={4 + (showSip ? 5 : 0) + (showQr ? 1 : 0)} className="px-4 py-6 text-center text-sm text-zinc-400">
                      Aucune chambre ne correspond aux filtres.
                    </td>
                  </tr>
                )}
              </tbody>
            </>);
          })()}
        </table>
      </div>

      {/* Pagination */}
      {(() => {
        const filteredRooms = rooms.filter(roomMatches);
        const totalPages = Math.max(1, Math.ceil(filteredRooms.length / PAGE_SIZE));
        if (totalPages <= 1) return null;
        const safePage = Math.min(page, totalPages);
        return (
          <div className="mt-2 flex items-center justify-between font-mono text-xs text-zinc-500">
            <span>{filteredRooms.length} chambre(s) · page {safePage}/{totalPages}</span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(1)} disabled={safePage === 1} className="rounded-sm border border-zinc-200 px-2 py-1 hover:bg-zinc-50 disabled:opacity-30">«</button>
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage === 1} className="rounded-sm border border-zinc-200 px-2 py-1 hover:bg-zinc-50 disabled:opacity-30">‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - safePage) <= 1)
                .reduce((acc, p, i, arr) => {
                  if (i > 0 && p - arr[i - 1] > 1) acc.push("…");
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) => p === "…"
                  ? <span key={`ellipsis-${i}`} className="px-1">…</span>
                  : <button key={p} onClick={() => setPage(p)} className={`rounded-sm border px-2 py-1 ${p === safePage ? "border-blue-500 bg-blue-50 text-blue-700" : "border-zinc-200 hover:bg-zinc-50"}`}>{p}</button>
                )}
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} className="rounded-sm border border-zinc-200 px-2 py-1 hover:bg-zinc-50 disabled:opacity-30">›</button>
              <button onClick={() => setPage(totalPages)} disabled={safePage === totalPages} className="rounded-sm border border-zinc-200 px-2 py-1 hover:bg-zinc-50 disabled:opacity-30">»</button>
            </div>
          </div>
        );
      })()}

      {selected.size > 0 && (
        <div className="mt-3 flex items-center gap-3 rounded-sm border border-blue-200 bg-blue-50 px-4 py-3">
          <span className="text-xs font-semibold text-blue-700">{selected.size} chambre(s) sélectionnée(s)</span>
          <div className="flex items-center gap-2">
            <select
              value={bulkPhone.vendor}
              onChange={(e) => setBulkPhone({ vendor: e.target.value, model: "" })}
              className="rounded-sm border border-zinc-200 bg-white px-2 py-1.5 text-xs text-zinc-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
            >
              <option value="">— Fabricant —</option>
              <option value="yealink">Yealink</option>
              <option value="fanvil">Fanvil</option>
              <option value="cisco">Cisco</option>
            </select>
            {bulkPhone.vendor && PHONE_MODELS_MAP[bulkPhone.vendor] && (
              <select
                value={bulkPhone.model}
                onChange={(e) => setBulkPhone((b) => ({ ...b, model: e.target.value }))}
                className="rounded-sm border border-zinc-200 bg-white px-2 py-1.5 text-xs text-zinc-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
              >
                <option value="">— Modèle —</option>
                {PHONE_MODELS_MAP[bulkPhone.vendor].map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            )}
            <Button
              onClick={applyBulkPhone}
              disabled={!bulkPhone.vendor || applyingBulk}
              className="rounded-sm bg-blue-600 px-3 py-1.5 text-xs hover:bg-blue-700"
            >
              {applyingBulk ? "Enregistrement…" : "Appliquer"}
            </Button>
          </div>
          <button onClick={() => setSelected(new Set())} className="ml-auto text-xs text-zinc-400 hover:text-zinc-600">
            Désélectionner tout
          </button>
        </div>
      )}

      {drawerRoomId && (
        <PhoneConfigDrawer
          hotelId={hotelId}
          hotel={hotel}
          rooms={rooms}
          roomId={drawerRoomId}
          onClose={() => setDrawerRoomId(null)}
          onRoomChange={(id) => setDrawerRoomId(id)}
          onUpdate={load}
        />
      )}

      {/* Modal QR par chambre */}
      <Dialog open={!!qrRoom} onOpenChange={(o) => !o && setQrRoom(null)}>
        <DialogContent className="rounded-sm max-w-sm">
          <DialogHeader>
            <DialogTitle>Chambre {qrRoom?.room_number}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-3 py-2">
            {qrDataUrl
              ? <img src={qrDataUrl} alt="QR" className="mx-auto" />
              : <div className="h-40 w-40 animate-pulse rounded-sm bg-zinc-100" />
            }
            {qrRoom?.qr_url && (
              <p className="max-w-full break-all font-mono text-[11px] text-zinc-500">{qrRoom.qr_url}</p>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-sm" onClick={downloadPng} disabled={!qrDataUrl}>
              <Download className="mr-1.5 h-3.5 w-3.5" /> Télécharger PNG
            </Button>
            <Button variant="outline" className="rounded-sm" onClick={() => setQrRoom(null)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------------------- Trunk ---------------------------- */
function TrunkTab({ hotelId, onChange }) {
  const [trunk, setTrunk] = useState({
    name: "3CX-Trunk",
    trunk_host: "",
    trunk_port: 5060,
    auth_id: "",
    auth_password: "",
    register: true,
    transport: "udp",
  });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get(`/hotels/${hotelId}/trunk`).then((r) => {
      if (r.data) setTrunk((t) => ({ ...t, ...r.data }));
      setLoaded(true);
    });
  }, [hotelId]);

  const save = async () => {
    try {
      await api.put(`/hotels/${hotelId}/trunk`, {
        ...trunk,
        trunk_port: Number(trunk.trunk_port),
        codecs: ["g722", "alaw", "ulaw"],
      });
      toast.success("Trunk 3CX enregistré");
      onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (!loaded) return <div className="mt-4 font-mono text-sm text-zinc-400">Chargement…</div>;

  return (
    <div className="mt-4 max-w-2xl space-y-4">
      <TrunkHealth hotelId={hotelId} trunkMeta={trunk} />
      <div className="rounded-sm border border-zinc-200 bg-white p-6">
      <h3 className="font-head text-lg font-bold tracking-tight text-zinc-900">
        Liaison Trunk SIP ↔ 3CX
      </h3>
      <p className="mt-1 text-sm text-zinc-500">
        Configure le trunk reliant le PBX 3CX de l'hôtel à son serveur Asterisk.
      </p>
      <div className="mt-5 grid grid-cols-2 gap-4">
        <TField label="Nom du trunk" testid="trunk-name" value={trunk.name} onChange={(v) => setTrunk({ ...trunk, name: v })} />
        <TField label="Transport" testid="trunk-transport" value={trunk.transport} onChange={(v) => setTrunk({ ...trunk, transport: v })} />
        <TField label="Hôte 3CX (FQDN/IP)" testid="trunk-host" value={trunk.trunk_host} onChange={(v) => setTrunk({ ...trunk, trunk_host: v })} placeholder="lutece.3cx.eu" />
        <TField label="Port" testid="trunk-port" value={trunk.trunk_port} onChange={(v) => setTrunk({ ...trunk, trunk_port: v })} />
        <TField label="Auth ID" testid="trunk-authid" value={trunk.auth_id} onChange={(v) => setTrunk({ ...trunk, auth_id: v })} />
        <TField label="Auth Password" testid="trunk-authpass" value={trunk.auth_password} onChange={(v) => setTrunk({ ...trunk, auth_password: v })} />
      </div>
      <label className="mt-4 flex items-center gap-2 text-sm text-zinc-700">
        <input
          data-testid="trunk-register-checkbox"
          type="checkbox"
          checked={trunk.register}
          onChange={(e) => setTrunk({ ...trunk, register: e.target.checked })}
          className="h-4 w-4 rounded-sm"
        />
        Enregistrement (REGISTER) vers le 3CX
      </label>
      <Button data-testid="save-trunk-btn" onClick={save} className="mt-5 rounded-sm bg-blue-600 hover:bg-blue-700">
        Enregistrer le trunk
      </Button>
      </div>
    </div>
  );
}

function fmtDt(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm} ${hh}:${min}`;
}

function TrunkHealth({ hotelId, trunkMeta }) {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [auto, setAuto] = useState(true);

  const check = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get(`/hotels/${hotelId}/trunk/health`);
      setHealth(data);
      if (!silent && !data.ok) toast.error(data.error);
    } catch (e) {
      if (!silent) toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    check(true);
    if (!auto) return undefined;
    const id = setInterval(() => check(true), 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelId, auto]);

  const up = health?.ok && health?.registered;
  return (
    <div className="flex items-center justify-between rounded-sm border border-zinc-200 bg-white px-5 py-4">
      <div className="flex items-center gap-3">
        <Activity className={`h-5 w-5 ${up ? "text-emerald-500" : "text-zinc-400"}`} />
        <div>
          <div className="font-head text-sm font-bold tracking-tight text-zinc-900">
            Santé du trunk 3CX
          </div>
          <div data-testid="trunk-health-status" className="font-mono text-xs text-zinc-500">
            {health == null
              ? "Vérification…"
              : health.ok
              ? up
                ? "Trunk ENREGISTRÉ auprès du 3CX"
                : "Trunk NON enregistré"
              : health.error}
          </div>
          {trunkMeta?.last_up && (
            <div className="font-mono text-xs text-zinc-400">
              Dernière connexion : {fmtDt(trunkMeta.last_up)}
            </div>
          )}
          {trunkMeta?.last_down && !up && (
            <div className="font-mono text-xs text-amber-500">
              Déconnecté depuis : {fmtDt(trunkMeta.last_down)}
            </div>
          )}
          {trunkMeta?.flapping_count > 2 && (
            <div className="font-mono text-xs text-red-500">
              Instabilités : {trunkMeta.flapping_count}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <label className="flex cursor-pointer items-center gap-1.5 font-mono text-xs text-zinc-500">
          <input
            data-testid="trunk-auto-refresh"
            type="checkbox"
            checked={auto}
            onChange={(e) => setAuto(e.target.checked)}
            className="h-3.5 w-3.5 rounded-sm"
          />
          Auto 30s
        </label>
        <button
          data-testid="check-trunk-health-btn"
          onClick={() => check(false)}
          disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-sm border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Vérifier
        </button>
      </div>
    </div>
  );
}

function TField({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <Lbl>{label}</Lbl>
      <Input data-testid={`${testid}-input`} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="mt-1 rounded-sm font-mono text-sm" />
    </div>
  );
}

/* ---------------------------- Speed dials (type 9) ---------------------------- */
function KeysTab({ hotelId }) {
  const [keys, setKeys] = useState([]);
  const [form, setForm] = useState({ key: "9", label: "", target: "", via: "trunk" });

  const load = () => api.get(`/hotels/${hotelId}/speed-dials`).then((r) => setKeys(r.data));
  useEffect(() => {
    load();
  }, [hotelId]);

  const add = async () => {
    try {
      await api.post(`/hotels/${hotelId}/speed-dials`, form);
      toast.success("Touche ajoutée");
      setForm({ key: "9", label: "", target: "", via: "trunk" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const del = async (sid) => {
    try {
      await api.delete(`/speed-dials/${sid}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="mt-4 grid gap-6 lg:grid-cols-3">
      <div className="lg:col-span-2 overflow-hidden rounded-sm border border-zinc-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-200 text-left text-[10px] uppercase tracking-[0.15em] text-zinc-500">
              <th className="px-4 py-2 font-bold">Touche</th>
              <th className="px-4 py-2 font-bold">Libellé</th>
              <th className="px-4 py-2 font-bold">Cible</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.id} data-testid={`key-row-${k.key}`} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50">
                <td className="px-4 py-3">
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-sm bg-zinc-900 font-mono text-sm font-bold text-white">
                    {k.key}
                  </span>
                </td>
                <td className="px-4 py-3 font-medium text-zinc-900">{k.label}</td>
                <td className="px-4 py-3">
                  <span className="font-mono text-xs text-zinc-700">{k.target}</span>
                  <span className="ml-2 inline-flex items-center rounded-sm border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 font-mono text-[10px] text-zinc-500">
                    {k.via === "local" ? "locale" : "3CX"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`delete-key-${k.key}`} onClick={() => del(k.id)} className="text-zinc-400 hover:text-red-600">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
            {keys.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-sm text-zinc-400">
                  Aucune touche configurée.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="rounded-sm border border-zinc-200 bg-white p-5">
        <h3 className="font-head font-bold tracking-tight text-zinc-900">Nouvelle touche</h3>
        <p className="mt-1 text-xs text-zinc-500">
          Ex : touche 9 → Réception, 8 → Room Service.
        </p>
        <div className="mt-4 space-y-3">
          <div>
            <Lbl>Touche</Lbl>
            <Input data-testid="key-number-input" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} className="mt-1 rounded-sm font-mono text-sm" />
          </div>
          <div>
            <Lbl>Libellé</Lbl>
            <Input data-testid="key-label-input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="mt-1 rounded-sm text-sm" placeholder="Réception" />
          </div>
          <div>
            <Lbl>Cible (extension / numéro)</Lbl>
            <Input data-testid="key-target-input" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} className="mt-1 rounded-sm font-mono text-sm" placeholder="100" />
          </div>
          <div>
            <Lbl>Destination</Lbl>
            <select
              data-testid="key-via-select"
              value={form.via}
              onChange={(e) => setForm({ ...form, via: e.target.value })}
              className="mt-1 h-9 w-full rounded-sm border border-zinc-200 bg-white px-2 text-sm"
            >
              <option value="trunk">Extension 3CX (via le trunk)</option>
              <option value="local">Extension locale</option>
            </select>
          </div>
          <Button data-testid="save-key-btn" onClick={add} disabled={!form.label || !form.target} className="w-full rounded-sm bg-blue-600 hover:bg-blue-700">
            Ajouter la touche
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------- Config / export ---------------------------- */
function ConfigTab({ hotelId, hotelName }) {
  const [asterisk, setAsterisk] = useState(null);

  const loadAsterisk = () =>
    api.get(`/hotels/${hotelId}/config/asterisk`).then((r) => setAsterisk(r.data));
  useEffect(() => {
    loadAsterisk();
  }, [hotelId]);

  const download = (content, filename, type = "text/plain") => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const export3cx = async () => {
    const { data } = await api.get(`/hotels/${hotelId}/config/3cx`);
    download(JSON.stringify(data, null, 2), `3cx-${hotelName}.json`, "application/json");
    toast.success("Export 3CX téléchargé");
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex flex-wrap gap-2">
        <Button
          data-testid="export-3cx-config"
          onClick={export3cx}
          className="rounded-sm bg-blue-600 hover:bg-blue-700"
        >
          <Download className="mr-1 h-4 w-4" /> Exporter config 3CX (JSON)
        </Button>
        <Button
          data-testid="download-asterisk-config"
          variant="outline"
          onClick={() => asterisk && download(asterisk.config, asterisk.filename)}
          className="rounded-sm"
        >
          <Download className="mr-1 h-4 w-4" /> Télécharger pjsip.conf
        </Button>
      </div>

      <div className="overflow-hidden rounded-sm border border-zinc-200 bg-zinc-950">
        <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-2">
          <span className="font-mono text-xs text-zinc-400">
            {asterisk?.filename || "pjsip.conf"}
          </span>
          <button
            data-testid="copy-asterisk-config"
            onClick={() => {
              navigator.clipboard.writeText(asterisk?.config || "");
              toast.success("Configuration copiée");
            }}
            className="inline-flex items-center gap-1 font-mono text-xs text-zinc-400 hover:text-white"
          >
            <Copy className="h-3.5 w-3.5" /> Copier
          </button>
        </div>
        <pre
          data-testid="asterisk-config-preview"
          className="max-h-[460px] overflow-auto p-4 font-mono text-[11px] leading-relaxed text-zinc-300"
        >
          {asterisk?.config || "Génération…"}
        </pre>
      </div>
    </div>
  );
}

/* ---------------------------- CDR ---------------------------- */
function CdrTab({ hotelId }) {
  const PAGE = 25;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = async (newOffset = offset, df = dateFrom, dt = dateTo) => {
    setLoading(true);
    try {
      const params = { limit: PAGE, offset: newOffset };
      if (df) params.date_from = df;
      if (dt) params.date_to = dt;
      const { data } = await api.get(`/hotels/${hotelId}/cdr`, { params });
      setData(data);
      if (!data.ok) toast.error(data.error);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(0);
    setOffset(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelId]);

  const applyFilters = () => {
    setOffset(0);
    load(0);
  };
  const resetFilters = () => {
    setDateFrom("");
    setDateTo("");
    setOffset(0);
    load(0, "", "");
  };
  const goto = (newOffset) => {
    setOffset(newOffset);
    load(newOffset);
  };

  const disp = (d) => {
    const map = {
      ANSWERED: "bg-emerald-50 text-emerald-700 border-emerald-200",
      "NO ANSWER": "bg-amber-50 text-amber-700 border-amber-200",
      BUSY: "bg-amber-50 text-amber-700 border-amber-200",
      FAILED: "bg-red-50 text-red-700 border-red-200",
    };
    return map[d] || "bg-zinc-100 text-zinc-600 border-zinc-200";
  };

  const rows = data?.rows || [];
  const total = data?.total || 0;
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + PAGE, total);

  return (
    <div className="mt-4">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Lbl>Du</Lbl>
            <Input
              data-testid="cdr-date-from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="mt-1 h-9 rounded-sm font-mono text-xs"
            />
          </div>
          <div>
            <Lbl>Au</Lbl>
            <Input
              data-testid="cdr-date-to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="mt-1 h-9 rounded-sm font-mono text-xs"
            />
          </div>
          <Button data-testid="cdr-apply-filters" onClick={applyFilters} className="h-9 rounded-sm bg-blue-600 hover:bg-blue-700">
            Filtrer
          </Button>
          <button data-testid="cdr-reset-filters" onClick={resetFilters} className="h-9 px-2 text-xs text-zinc-500 hover:text-zinc-900">
            Réinitialiser
          </button>
        </div>
        <button
          data-testid="refresh-cdr-btn"
          onClick={() => load(offset)}
          disabled={loading}
          className="inline-flex h-9 items-center gap-1.5 rounded-sm border border-zinc-300 bg-white px-3 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Actualiser
        </button>
      </div>

      <div className="overflow-hidden rounded-sm border border-zinc-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-200 text-left text-[10px] uppercase tracking-[0.15em] text-zinc-500">
              <th className="px-4 py-2 font-bold">Date</th>
              <th className="px-4 py-2 font-bold">Source</th>
              <th className="px-4 py-2 font-bold">Destination</th>
              <th className="px-4 py-2 font-bold">Durée</th>
              <th className="px-4 py-2 font-bold">Facturé</th>
              <th className="px-4 py-2 font-bold">Statut</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} data-testid={`cdr-row-${i}`} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50">
                <td className="px-4 py-2.5 font-mono text-xs text-zinc-600">{r.start}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-zinc-800">{r.src}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-zinc-800">{r.dst}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-zinc-600">{r.duration}s</td>
                <td className="px-4 py-2.5 font-mono text-xs text-zinc-600">{r.billsec}s</td>
                <td className="px-4 py-2.5">
                  <span className={`inline-flex items-center rounded-sm border px-2 py-0.5 font-mono text-[11px] ${disp(r.disposition)}`}>
                    {r.disposition || "—"}
                  </span>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-zinc-400">
                  {data && !data.ok
                    ? "Serveur injoignable — déployez un Asterisk et configurez l'agent."
                    : "Aucun appel pour ces critères."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex items-center justify-between">
        <span data-testid="cdr-pagination-info" className="font-mono text-xs text-zinc-500">
          {from}–{to} sur {total}
        </span>
        <div className="flex items-center gap-2">
          <button
            data-testid="cdr-prev-page"
            onClick={() => goto(Math.max(0, offset - PAGE))}
            disabled={offset === 0 || loading}
            className="rounded-sm border border-zinc-300 bg-white px-3 py-1.5 text-xs text-zinc-700 disabled:opacity-40 hover:bg-zinc-50"
          >
            Précédent
          </button>
          <button
            data-testid="cdr-next-page"
            onClick={() => goto(offset + PAGE)}
            disabled={to >= total || loading}
            className="rounded-sm border border-zinc-300 bg-white px-3 py-1.5 text-xs text-zinc-700 disabled:opacity-40 hover:bg-zinc-50"
          >
            Suivant
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------- Urgences (trunk externe) ---------------------------- */
function EmergencyTab({ hotelId }) {
  const DEFAULT_NUMS = "112,15,17,18,114,115,119,116000,116117";
  const [t, setT] = useState({
    name: "Trunk Urgences",
    provider_host: "",
    provider_port: 5060,
    auth_id: "",
    auth_password: "",
    register: true,
    transport: "udp",
    enabled: true,
  });
  const [nums, setNums] = useState(DEFAULT_NUMS);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get(`/hotels/${hotelId}/external-trunk`).then((r) => {
      if (r.data) {
        setT((p) => ({ ...p, ...r.data }));
        if (Array.isArray(r.data.emergency_numbers))
          setNums(r.data.emergency_numbers.join(","));
      }
      setLoaded(true);
    });
  }, [hotelId]);

  const save = async () => {
    try {
      await api.put(`/hotels/${hotelId}/external-trunk`, {
        ...t,
        provider_port: Number(t.provider_port),
        codecs: ["alaw", "ulaw"],
        emergency_numbers: nums.split(",").map((n) => n.trim()).filter(Boolean),
      });
      toast.success("Trunk d'urgence enregistré");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (!loaded) return <div className="mt-4 font-mono text-sm text-zinc-400">Chargement…</div>;

  return (
    <div className="mt-4 max-w-2xl rounded-sm border border-zinc-200 bg-white p-6">
      <div className="flex items-start gap-3">
        <Siren className="mt-0.5 h-5 w-5 text-red-500" />
        <div>
          <h3 className="font-head text-lg font-bold tracking-tight text-zinc-900">
            Trunk SIP externe — Appels d'urgence (France)
          </h3>
          <p className="mt-1 text-sm text-zinc-500">
            Les chambres ne peuvent pas s'appeler entre elles, mais les numéros d'urgence sont
            routés vers cet opérateur SIP externe.
          </p>
        </div>
      </div>

      <label className="mt-5 flex items-center gap-2 text-sm text-zinc-700">
        <input
          data-testid="emergency-enabled"
          type="checkbox"
          checked={t.enabled}
          onChange={(e) => setT({ ...t, enabled: e.target.checked })}
          className="h-4 w-4 rounded-sm"
        />
        Activer le trunk d'urgence
      </label>

      <div className="mt-4 grid grid-cols-2 gap-4">
        <TField label="Nom" testid="emg-name" value={t.name} onChange={(v) => setT({ ...t, name: v })} />
        <TField label="Transport" testid="emg-transport" value={t.transport} onChange={(v) => setT({ ...t, transport: v })} />
        <TField label="Hôte opérateur (FQDN/IP)" testid="emg-host" value={t.provider_host} onChange={(v) => setT({ ...t, provider_host: v })} placeholder="sip.operateur-urgence.fr" />
        <TField label="Port" testid="emg-port" value={t.provider_port} onChange={(v) => setT({ ...t, provider_port: v })} />
        <TField label="Auth ID" testid="emg-authid" value={t.auth_id} onChange={(v) => setT({ ...t, auth_id: v })} />
        <TField label="Auth Password" testid="emg-authpass" value={t.auth_password} onChange={(v) => setT({ ...t, auth_password: v })} />
      </div>

      <div className="mt-4">
        <Lbl>Numéros d'urgence (séparés par des virgules)</Lbl>
        <Input
          data-testid="emergency-numbers-input"
          value={nums}
          onChange={(e) => setNums(e.target.value)}
          className="mt-1 rounded-sm font-mono text-sm"
        />
        <p className="mt-1 font-mono text-[11px] text-zinc-400">
          Défaut FR : 112 (UE), 15 (SAMU), 17 (Police), 18 (Pompiers), 114, 115, 119, 116000, 116117
        </p>
      </div>

      <label className="mt-4 flex items-center gap-2 text-sm text-zinc-700">
        <input
          data-testid="emergency-register"
          type="checkbox"
          checked={t.register}
          onChange={(e) => setT({ ...t, register: e.target.checked })}
          className="h-4 w-4 rounded-sm"
        />
        Enregistrement (REGISTER) auprès de l'opérateur
      </label>

      <Button data-testid="save-emergency-btn" onClick={save} className="mt-5 rounded-sm bg-red-600 hover:bg-red-700">
        Enregistrer le trunk d'urgence
      </Button>
    </div>
  );
}

function Lbl({ children }) {
  return (
    <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
      {children}
    </Label>
  );
}

/* ---------------------------- Hotel admins ---------------------------- */
function UsersTab({ hotelId }) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "" });

  const load = () => api.get(`/hotels/${hotelId}/users`).then((r) => setUsers(r.data));
  useEffect(() => {
    load();
  }, [hotelId]);

  const add = async () => {
    try {
      await api.post(`/hotels/${hotelId}/users`, form);
      toast.success("Administrateur hôtel créé");
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const del = async (uid) => {
    try {
      await api.delete(`/users/${uid}`);
      toast.success("Administrateur supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="mt-4 grid gap-6 lg:grid-cols-3">
      <div className="lg:col-span-2 overflow-hidden rounded-sm border border-zinc-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-200 text-left text-[10px] uppercase tracking-[0.15em] text-zinc-500">
              <th className="px-4 py-2 font-bold">Nom</th>
              <th className="px-4 py-2 font-bold">Email</th>
              <th className="px-4 py-2 font-bold">Rôle</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.email}`} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50">
                <td className="px-4 py-3 font-medium text-zinc-900">{u.name}</td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-700">{u.email}</td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center rounded-sm border border-zinc-200 bg-zinc-50 px-2 py-0.5 font-mono text-[11px] text-zinc-600">
                    hotel_admin
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`delete-user-${u.email}`} onClick={() => del(u.id)} className="text-zinc-400 hover:text-red-600">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-sm text-zinc-400">
                  Aucun administrateur hôtel. Il ne verra et ne gérera que cet hôtel.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="rounded-sm border border-zinc-200 bg-white p-5">
        <h3 className="font-head font-bold tracking-tight text-zinc-900">Nouvel administrateur</h3>
        <p className="mt-1 text-xs text-zinc-500">Accès restreint à cet hôtel uniquement.</p>
        <div className="mt-4 space-y-3">
          <div>
            <Lbl>Nom</Lbl>
            <Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm text-sm" placeholder="Jean Dupont" />
          </div>
          <div>
            <Lbl>Email</Lbl>
            <Input data-testid="user-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-sm font-mono text-sm" placeholder="manager@hotel.io" />
          </div>
          <div>
            <Lbl>Mot de passe</Lbl>
            <Input data-testid="user-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 rounded-sm font-mono text-sm" placeholder="••••••••" />
          </div>
          <Button data-testid="save-user-btn" onClick={add} disabled={!form.name || !form.email || !form.password} className="w-full rounded-sm bg-blue-600 hover:bg-blue-700">
            Créer l'administrateur
          </Button>
        </div>
      </div>

      {/* Dialog suppression hôtel */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-red-700">Supprimer {hotel.name} ?</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm text-zinc-600">
            <p>
              Cette action est <strong>irréversible</strong>. Chambres, trunks, touches type 9 et utilisateurs liés seront supprimés définitivement.
            </p>
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                Domaine SIP de l'hôtel <span className="font-mono text-zinc-700">({hotel.sip_domain})</span>
              </label>
              <input
                autoFocus
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder={hotel.sip_domain}
                className="w-full rounded-sm border border-zinc-300 px-3 py-2 font-mono text-sm focus:outline-none focus:ring-1 focus:ring-red-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                Votre mot de passe
              </label>
              <input
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="••••••••"
                className="w-full rounded-sm border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-red-400"
              />
            </div>
          </div>
          <DialogFooter className="mt-2 gap-2">
            <button
              onClick={() => setDeleteOpen(false)}
              className="rounded-sm border border-zinc-200 px-4 py-2 text-sm text-zinc-600 hover:bg-zinc-50"
            >
              Annuler
            </button>
            <button
              onClick={handleDelete}
              disabled={deleteConfirm !== hotel.sip_domain || !deletePassword || deleting}
              className="rounded-sm bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-40"
            >
              {deleting ? "Suppression…" : "Supprimer définitivement"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
